import { useState, type FormEvent } from 'react';

interface ServiceAreaHintSectionProps {
  /** 輸入後送出（呼叫 find_google_place_peers 走 SERVICE_AREA 路徑） */
  onSubmit: (serviceAreaHint: string) => void;
  /** 是否正在建立同業樣本 */
  loading: boolean;
  /** 帶入的預設值（例如使用者第一階段輸入的城市） */
  defaultHint?: string;
}

/**
 * 服務區域商家沒有公開實體地址時，請使用者補填主要服務城市／行政區，
 * 才能建立合理的同業比較範圍。僅在 Service Area Business 情境顯示。
 */
export default function ServiceAreaHintSection({
  onSubmit,
  loading,
  defaultHint = '',
}: ServiceAreaHintSectionProps) {
  const [hint, setHint] = useState(defaultHint);
  const [error, setError] = useState('');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;
    const trimmed = hint.trim();
    if (trimmed.length < 2) {
      setError('請輸入主要服務城市或行政區，例如：台中、西屯區。');
      return;
    }
    setError('');
    onSubmit(trimmed);
  };

  return (
    <section className="ds-container pt-6">
      <div className="rounded-2xl border border-accent-200 bg-accent-50/70 p-5 md:p-6">
        <div className="flex items-start gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-background-50 text-accent-700 ring-1 ring-accent-200">
            <i className="ri-service-line text-xl" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-accent-900">這是服務區域商家，沒有公開實體地址。</p>
            <p className="mt-1.5 text-base text-accent-900/90">
              請輸入主要服務城市或行政區，才能建立合理的同業比較範圍。
            </p>

            <form onSubmit={handleSubmit} noValidate className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex flex-1 items-center gap-3 rounded-xl border border-accent-300 bg-background-50 px-4 py-3.5 transition-colors focus-within:border-accent-500">
                <i className="ri-map-pin-line text-xl text-accent-500" />
                <input
                  type="text"
                  name="serviceAreaHint"
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                  placeholder="例如：台中、西屯區、高雄左營"
                  disabled={loading}
                  className="w-full bg-transparent text-base text-foreground-950 placeholder:text-foreground-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex h-14 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-700 px-6 text-base font-semibold text-background-50 transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-lg" />
                    建立中…
                  </>
                ) : (
                  <>
                    <i className="ri-community-line text-lg" />
                    建立同業樣本
                  </>
                )}
              </button>
            </form>

            {error && (
              <p className="mt-3 flex items-center gap-2 text-base text-rose-600">
                <i className="ri-error-warning-line text-lg" />
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}